/**
 * ブラウザのライフサイクルイベントを監視し、必要に応じて reconnect を呼ぶ。
 *
 * 主な用途:
 * - MacBook スリープ復帰時の ePOS SDK ゾンビ WebSocket 蓄積回避
 * - WiFi 切断 → 復帰時の明示的な再接続
 *
 * 検知ロジック:
 * - visibilitychange: 30秒以上の hidden → visible で再接続
 * - online: 30秒以上の offline → online で再接続
 * - 直前の reconnect から10秒以内は抑止（多重トリガー防止）
 *
 * module-level でリスナー登録するため、複数回呼んでも1度しか登録されない。
 */

const RESUME_THRESHOLD_MS = 30_000
const TRIGGER_COOLDOWN_MS = 10_000

let registered = false
const reconnectFns = new Set()

let lastHiddenAt = null
let lastOfflineAt = null
let lastReconnectAt = 0

const trigger = async source => {
  const now = Date.now()
  if (now - lastReconnectAt < TRIGGER_COOLDOWN_MS) return
  lastReconnectAt = now

  await Promise.allSettled(
    Array.from(reconnectFns).map(fn => fn().catch(error => {
      // eslint-disable-next-line no-console
      console.error(`[printer-reconnect] reconnect failed (${source}):`, error)
    })),
  )
}

const onVisibilityChange = () => {
  if (document.hidden) {
    lastHiddenAt = Date.now()

    return
  }
  if (lastHiddenAt && Date.now() - lastHiddenAt >= RESUME_THRESHOLD_MS) {
    trigger('visibility')
  }
  lastHiddenAt = null
}

const onOnline = () => {
  if (lastOfflineAt && Date.now() - lastOfflineAt >= RESUME_THRESHOLD_MS) {
    trigger('online')
  }
  lastOfflineAt = null
}

const onOffline = () => {
  lastOfflineAt = Date.now()
}

const ensureListenersRegistered = () => {
  if (registered) return
  registered = true
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
}

/**
 * @param {() => Promise<unknown>} reconnectFn
 */
export default function usePrinterReconnectOnResume(reconnectFn) {
  ensureListenersRegistered()
  reconnectFns.add(reconnectFn)
}
